import React from 'react';
import styles from './LoadingText.module.css';

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children] - Children to animate if string
 * @param {string} [props.text] - Plain text to animate
 * @param {string} [props.className] - External container class
 * @param {string} [props.charClassName] - Class applied to each character span
 * @param {number} [props.speed=1.4] - Duration of animation in seconds
 * @param {number} [props.stagger=0.15] - Delay between characters in seconds
 */
const LoadingText = ({
  children,
  text,
  className = '',
  charClassName = '',
  speed = 1.4,
  stagger = 0.15,
}) => {
  const content = text || (typeof children === 'string' ? children : '');

  if (!content) {
    return <>{children}</>;
  }

  // Split string into words and preserve spaces
  const words = content.split(' ');
  let charIndex = 0;

  return (
    <span className={[styles.textContainer, className].filter(Boolean).join(' ')}>
      {words.map((word, wordIdx) => {
        const chars = word.split('');
        const renderedWord = (
          <span key={`word-${wordIdx}`} className={styles.word}>
            {chars.map((char) => {
              const delay = charIndex * stagger;
              charIndex++;
              return (
                <span
                  key={`char-${charIndex}`}
                  className={[styles.char, charClassName].filter(Boolean).join(' ')}
                  style={{
                    animationDelay: `${delay}s`,
                    animationDuration: `${speed}s`,
                  }}
                >
                  {char}
                </span>
              );
            })}
          </span>
        );

        // Add an animated space between words to maintain rhythm
        const spaceDelay = charIndex * stagger;
        charIndex++;

        return (
          <React.Fragment key={`word-wrapper-${wordIdx}`}>
            {renderedWord}
            {wordIdx < words.length - 1 && (
              <span
                className={[styles.char, charClassName].filter(Boolean).join(' ')}
                style={{
                  animationDelay: `${spaceDelay}s`,
                  animationDuration: `${speed}s`,
                }}
              >
                {'\u00A0'}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </span>
  );
};

export default LoadingText;
